import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Mail, LogIn, User, LogOut, BookOpen, MapPin, Home, BedDouble, Menu, X, Info, HelpCircle } from 'lucide-react';
import { useUser } from '../../context/UserContext';

const NAV_LINKS = [
  { to: '/', label: 'Home', icon: Home },
  { to: '/#stations', label: 'Stations', icon: BedDouble },
  { to: '/#room-types', label: 'Rooms', icon: BedDouble },
  { to: '/#about', label: 'About', icon: Info },
  { to: '/blog', label: 'Blog', icon: BookOpen },
  { to: '/help', label: 'Help', icon: HelpCircle },
];

const PublicLayout = ({ children }) => {
  const { isAuthenticated, user, logout } = useUser();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleNavClick = (e, to) => {
    setMobileMenuOpen(false);

    // Home link — scroll to top
    if (to === '/' && location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      window.history.replaceState(null, '', '/');
      return;
    }

    if (!to.includes('#')) return;

    const [path, hash] = to.split('#');
    const targetPath = path || '/';

    if (location.pathname === targetPath) {
      e.preventDefault();
      const el = document.getElementById(hash);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        window.history.replaceState(null, '', `${targetPath}#${hash}`);
      }
    }
  };

  const isActive = (to) => {
    if (to === '/') return location.pathname === '/';
    if (to.startsWith('/#')) return location.pathname === '/';
    return location.pathname.startsWith(to);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="bg-gradient-to-r from-midnight-950 via-midnight-900 to-midnight-950 text-white shadow-2xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 lg:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0" onClick={(e) => { setMobileMenuOpen(false); if (location.pathname === '/') { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); window.history.replaceState(null, '', '/'); } }}>
              <img
                src="/Retiring Room Icon.svg"
                alt="Retiring Room logo — book comfortable accommodation at railway stations"
                className="w-10 h-10 lg:w-14 lg:h-14 transform group-hover:scale-110 transition-transform"
                width="56"
                height="56"
              />
              <div>
                <span className="text-lg lg:text-xl font-display font-bold tracking-tight block">Retiring Room</span>
                <span className="text-[10px] lg:text-xs text-railway-400 font-medium block leading-tight">By JUNES KITCHEN AND BAKERS</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" aria-label="Main navigation">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={(e) => handleNavClick(e, link.to)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${isActive(link.to)
                    ? 'text-white bg-white/10'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <link.icon className="w-4 h-4" aria-hidden="true" />
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Right Side: Auth + Mobile Toggle */}
            <div className="flex items-center gap-2">
              {/* Auth (Desktop) */}
              <div className="hidden lg:flex items-center gap-2">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/my-bookings"
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-medium transition-all ${isActive('/my-bookings')
                          ? 'bg-white/20 text-white'
                          : 'bg-white/10 hover:bg-white/20 text-white/80 hover:text-white'
                        } backdrop-blur-sm border border-white/20`}
                    >
                      <BookOpen className="w-4 h-4" aria-hidden="true" />
                      My Bookings
                    </Link>
                    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-3 py-1.5 rounded-xl">
                      <User className="w-4 h-4 text-railway-400" aria-hidden="true" />
                      <span className="text-sm font-medium">{user?.name?.split(' ')[0] || 'User'}</span>
                    </div>
                    <button
                      onClick={logout}
                      className="flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/20 px-3 py-1.5 rounded-xl text-sm font-medium transition-all"
                      aria-label="Log out of your account"
                    >
                      <LogOut className="w-4 h-4" aria-hidden="true" />
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/user/login"
                    className="flex items-center gap-2 bg-railway-500 hover:bg-railway-600 px-4 py-2 rounded-xl text-sm font-medium transition-all shadow-lg shadow-railway-500/30"
                  >
                    <LogIn className="w-4 h-4" aria-hidden="true" />
                    Login / Sign Up
                  </Link>
                )}
              </div>

              {/* Mobile Menu Toggle */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden flex items-center justify-center w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? (
                  <X className="w-5 h-5" aria-hidden="true" />
                ) : (
                  <Menu className="w-5 h-5" aria-hidden="true" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-white/10 bg-midnight-950/95 backdrop-blur-md">
            <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1" aria-label="Mobile navigation">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={(e) => handleNavClick(e, link.to)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${isActive(link.to)
                    ? 'text-white bg-white/10'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <link.icon className="w-5 h-5" aria-hidden="true" />
                  {link.label}
                </Link>
              ))}

              <div className="pt-3 mt-3 border-t border-white/10">
                {isAuthenticated ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3 px-4 py-3 text-white/60 text-sm">
                      <User className="w-5 h-5 text-railway-400" aria-hidden="true" />
                      <span className="font-medium text-white">{user?.name?.split(' ')[0] || 'User'}</span>
                    </div>
                    <Link
                      to="/my-bookings"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-white/80 hover:text-white hover:bg-white/5 transition-colors w-full"
                    >
                      <BookOpen className="w-5 h-5 text-railway-400" aria-hidden="true" />
                      My Bookings
                    </Link>
                    <button
                      onClick={() => { logout(); setMobileMenuOpen(false); }}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-red-400 hover:bg-white/5 transition-colors w-full"
                    >
                      <LogOut className="w-5 h-5" aria-hidden="true" />
                      Logout
                    </button>
                  </div>
                ) : (
                  <Link
                    to="/user/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 bg-railway-500 hover:bg-railway-600 px-4 py-3 rounded-xl text-sm font-medium transition-all shadow-lg shadow-railway-500/30"
                  >
                    <LogIn className="w-5 h-5" aria-hidden="true" />
                    Login / Sign Up
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-midnight-950 text-white py-16" role="contentinfo">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="/Retiring Room Icon.svg"
                  alt="Retiring Room logo"
                  className="w-14 h-14"
                  width="56"
                  height="56"
                  loading="lazy"
                />
                <span className="font-display font-bold text-lg">Retiring Room</span>
              </div>
              <p className="text-white/60 text-sm leading-relaxed">
                India's trusted platform for booking comfortable retiring rooms at major railway and transit stations.
                AC rooms, dormitories, and deluxe suites with instant online booking.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-display font-semibold text-lg mb-4">Quick Links</h4>
              <ul className="space-y-2.5" role="list">
                <li>
                  <Link to="/" className="text-white/60 hover:text-railway-400 transition-colors text-sm">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to="/#stations" className="text-white/60 hover:text-railway-400 transition-colors text-sm">
                    Find Stations
                  </Link>
                </li>
                <li>
                  <Link to="/blog" className="text-white/60 hover:text-railway-400 transition-colors text-sm">
                    Travel Guides & Blog
                  </Link>
                </li>
                <li>
                  <Link to="/user/login" className="text-white/60 hover:text-railway-400 transition-colors text-sm">
                    Login / Sign Up
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="text-white/60 hover:text-railway-400 transition-colors text-sm">
                    Admin Login
                  </Link>
                </li>
                <li>
                  <Link to="/terms-and-conditions" className="text-white/60 hover:text-railway-400 transition-colors text-sm">
                    Terms & Conditions
                  </Link>
                </li>
              </ul>
            </div>

            {/* Travel Guides */}
            <div>
              <h4 className="font-display font-semibold text-lg mb-4">Travel Guides</h4>
              <ul className="space-y-2.5" role="list">
                <li>
                  <Link to="/blog/travel-guide-bhusaval" className="text-white/60 hover:text-railway-400 transition-colors text-sm flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                    Bhusaval Travel Guide
                  </Link>
                </li>
                <li>
                  <Link to="/blog/travel-guide-kannur" className="text-white/60 hover:text-railway-400 transition-colors text-sm flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" aria-hidden="true" />
                    Kannur Travel Guide
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-display font-semibold text-lg mb-4">Contact Us</h4>
              <ul className="space-y-2.5" role="list">
                <li>
                  <a href="tel:+918983044786" className="flex items-center gap-2 text-white/60 hover:text-railway-400 transition-colors text-sm">
                    <Phone className="w-4 h-4" aria-hidden="true" />
                    <span>+91 8983044786</span>
                  </a>
                </li>
                <li>
                  <a href="tel:+918600447868" className="flex items-center gap-2 text-white/60 hover:text-railway-400 transition-colors text-sm">
                    <Phone className="w-4 h-4" aria-hidden="true" />
                    <span>+91 8600447868</span>
                  </a>
                </li>
                <li>
                  <a href="mailto:info@retiringroom.in" className="flex items-center gap-2 text-white/60 hover:text-railway-400 transition-colors text-sm">
                    <Mail className="w-4 h-4" aria-hidden="true" />
                    <span>info@retiringroom.in</span>
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4 text-white/40 text-sm">
            <p>&copy; {new Date().getFullYear()} Retiring Room. All rights reserved.</p>
            <p>Operated by Junes Kitchen and Bakers</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
